import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CalendarUtils from 'services/CalendarUtils';
import ListBadge from 'components/Common/ListBadge';
import PatientUtils from 'services/PatientUtils';
import DocumentUtils from 'services/DocumentUtils';
import Icon from 'components/Utils/NewIcon';
import ProgressMessage from 'components/Utils/ProgressMessage';
import MiscUtils from 'services/MiscUtils';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import './style.scss';

const dateFormat = 'Do MMM YYYY h:mm A';

const DocumentBadge = ({ item, patient, onClick, extraAlerts, ...props }) => {
  const documentId = item.id;

  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authentication.role);
  const flags = useAppSelector((state) => state.documents.flags[documentId]);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const hasResults = item.results_text?.length > 0;
  const isLockedForPatient = DocumentUtils.isLockedForPatient(item, role);
  const unlockDateText = isLockedForPatient ? DocumentUtils.getUnlockDateText(item) : '';

  useEffect(() => {
    if (hasResults && flags === undefined) {
      dispatch(api.Documents.getDocumentFlags({ documentId }));
    }
  }, [dispatch, hasResults, documentId, flags]);

  const flagsText = useMemo(
    () => DocumentUtils.getFlagsAndCommentsAlert(item, role, flags || '', physicians, admins),
    [item, role, flags, physicians, admins],
  );

  const alerts = useMemo(() => {
    let result = [];

    const showThumbnail = role !== 'patient' && !!item.thumbnail;

    let hasFlags = (flagsText || '').includes('Flags:');

    if (isLockedForPatient) {
      result.push(
        <Alert
          key="locked-alert"
          text={`Available on ${unlockDateText}`}
          icon="lock"
          style={{ color: '#888' }}
        />,
      );
    }

    if (showThumbnail) {
      result.push(<ThumbnailPreview url={item.thumbnail} key={item.thumbnail} />);
    }

    if (flagsText) {
      result.push(
        <Alert
          key="flags-alert"
          text={flagsText}
          icon={['fad', 'circle-exclamation']}
          style={{
            '--fa-primary-color': 'white',
            '--fa-secondary-color': hasFlags ? '#d16f3e' : '#2693D1',
            '--fa-secondary-opacity': 1.0,
          }}
        />,
      );
    }

    if (item.fee) {
      result.push(
        <Alert
          key="fee-alert"
          text={`${item.paid ? 'Paid' : 'Unpaid'} fee: $${item.fee}`}
          icon="file-invoice-dollar"
          style={{ color: item.paid ? '#2693D1' : '#d16f3e' }}
        />,
      );
    }

    if (item.has_repeat) {
      result.push(
        <Alert
          key="fee-alert"
          text={`Repeats every ${item.repeat_options?.number} ${item.repeat_options?.unit}`}
          icon="repeat"
          width="15px"
        />,
      );
    }

    // Merge extra alerts if provided
    if (extraAlerts) {
      result = [...extraAlerts, ...result];
    }

    return result;
  }, [
    flagsText,
    role,
    item.paid,
    item.fee,
    item.thumbnail,
    item.has_repeat,
    item.repeat_options,
    extraAlerts,
    isLockedForPatient,
    unlockDateText,
  ]);

  return (
    <ListBadge
      alerts={alerts}
      loading={!!item.processing}
      title={item.title}
      firstLine={
        props.firstLine ||
        (isLockedForPatient ? (
          <LockedFirstLine unlockDateText={unlockDateText} />
        ) : (
          <InfoText item={item} />
        ))
      }
      secondLine={props.secondLine || <SecondLine item={item} patient={patient} />}
      unread={role === 'patient' && !item.read}
      onClick={onClick}
      className={isLockedForPatient ? 'document-badge--locked' : ''}
    >
      {props.children}
    </ListBadge>
  );
};

const LockedFirstLine = ({ unlockDateText }) => (
  <span className="document-badge__locked-first-line">
    <Icon src="lock" width="14px" height="14px" /> Locked until {unlockDateText}
  </span>
);

export const ThumbnailPreview = ({ url, width = '200px', thumbnailStyle = {} }) => {
  const [data, setData] = useState();
  const dispatch = useAppDispatch();

  async function previewFile() {
    if (!data) {
      const result = await dispatch(api.Resource.getResource({ resource: { url } }));
      if (result) {
        const blobUrl = MiscUtils.getBlobUrl(result);
        setData(blobUrl);
      }
    }
  }

  return (
    <div className="thumbnail-wrapper" onMouseEnter={previewFile}>
      <div
        className={'thumbnail ' + (!data ? 'loading' : 'ready')}
        style={{ width, maxWidth: width, left: `-${width}`, ...thumbnailStyle }}
      >
        {data ? <img src={data} /> : <ProgressMessage size={35} />}
      </div>
      <Icon src="eye" showTooltip={false} tooltipPlace="left" width="20px" height="20px" />
    </div>
  );
};

export const Alert = ({ icon = 'circle-exclamation', style, text, width = '20px' }) => {
  return (
    <Icon
      src={icon || 'circle-exclamation'}
      showTooltip={true}
      iconStyle={
        style || {
          '--fa-primary-color': 'white',
          '--fa-secondary-color': '#d16f3e',
          '--fa-secondary-opacity': 1.0,
        }
      }
      tooltipPlace="left"
      alt={text}
      width={width}
      height={width}
    />
  );
};

const SecondLine = ({ item, patient }) =>
  !!patient ? (
    <>
      {CalendarUtils.format(item.date, dateFormat)}{' '}
      {PatientUtils.getName(patient, undefined, false)}
    </>
  ) : (
    CalendarUtils.format(item.date, dateFormat)
  );

const InfoText = ({ item }) =>
  item.document_type !== undefined ? <DocumentType item={item} /> : <DocumentRoute item={item} />;

const DocumentType = ({ item }) => {
  let type =
    item.patient_id === undefined ? 'Unassigned ' + item.document_type : item.document_type;

  if (item.referral_status) {
    type += ` - ${item.referral_status}`;
  }

  return type;
};

const DocumentRoute = ({ item }) => (
  <span>
    <span className="capitalize">{item.route}</span> <span>from: {item.from}</span>
  </span>
);

DocumentBadge.propTypes = {
  item: PropTypes.object.isRequired,
  patient: PropTypes.object,
  onClick: PropTypes.func,
};

export default DocumentBadge;
